import React, { useState, useRef, useEffect } from 'react';
import { executeCommand } from '../utils/commands';
import { getAutocompleteSuggestions, getBestMatch } from '../utils/autocomplete';

interface CommandHistory {
  command: string;
  output: string | React.ReactElement;
  timestamp: Date;
}

const Terminal: React.FC = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<CommandHistory[]>([
    {
      command: 'welcome',
      output: 'Welcome! Type "help" to see available commands.',
      timestamp: new Date(),
    },
  ]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentPath, setCurrentPath] = useState('~');
  const inputRef = useRef<HTMLInputElement>(null);
  const terminalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const command = input.trim().toLowerCase();
    const commandText = input.trim();
    
    // Komut geçmişine ekle
    setCommandHistory((prev) => {
      // Son komutla aynı değilse ekle
      if (prev.length === 0 || prev[prev.length - 1] !== commandText) {
        return [...prev, commandText];
      }
      return prev;
    });
    setHistoryIndex(-1);
    
    // Clear komutu için özel işlem
    if (command === 'clear') {
      setHistory([]);
      setInput('');
      return;
    }

    const output = executeCommand(commandText, setCurrentPath, currentPath, commandHistory);
    
    setHistory((prev) => [
      ...prev,
      {
        command: commandText,
        output,
        timestamp: new Date(),
      },
    ]);
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Ctrl+L ile temizle
    if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHistory([]);
      return;
    }

    // Ctrl+C ile iptal et (input'u temizle)
    if (e.key === 'c' && e.ctrlKey) {
      e.preventDefault();
      setInput('');
      return;
    }

    // Tab ile autocomplete
    if (e.key === 'Tab') {
      e.preventDefault();
      const suggestions = getAutocompleteSuggestions(input);
      const bestMatch = getBestMatch(input);
      
      if (bestMatch && suggestions.length > 0) {
        const parts = input.trim().split(' ');
        if (parts.length === 1) {
          // Komut autocomplete
          setInput(bestMatch + ' ');
        } else if (parts[0].toLowerCase() === 'cat' && parts.length === 2) {
          // Dosya autocomplete
          setInput(parts[0] + ' ' + bestMatch);
        } else if (parts[0].toLowerCase() === 'cd' && parts.length === 2) {
          // Dizin autocomplete
          setInput(parts[0] + ' ' + bestMatch);
        }
      }
      return;
    }

    // Üst ok tuşu - önceki komut
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      
      const newIndex = historyIndex === -1 
        ? commandHistory.length - 1 
        : Math.max(0, historyIndex - 1);
      
      setHistoryIndex(newIndex);
      setInput(commandHistory[newIndex]);
      return;
    }

    // Alt ok tuşu - sonraki komut
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return;
      
      const newIndex = historyIndex + 1;
      if (newIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInput('');
      } else {
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
      return;
    }

    // Herhangi bir tuşa basıldığında history index'i sıfırla
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      setHistoryIndex(-1);
    }
  };

  return (
    <div
      ref={terminalRef}
      className="h-screen w-full bg-[#0d1117] text-[#c9d1d9] font-mono p-8 overflow-y-auto selection:bg-[#264f78] selection:text-[#c9d1d9] terminal-scrollbar"
      onClick={() => inputRef.current?.focus()}
      tabIndex={0}
    >
      <div className="max-w-4xl mx-auto">
        {/* Terminal Header */}
        <div className="mb-8 pb-4 border-b border-[#21262d]">
          <div className="flex items-center gap-2 text-xs text-[#8b949e]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]"></span>
            <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]"></span>
            <span className="ml-3 text-[#8b949e]">ergoren@terminal</span>
          </div>
        </div>

        {/* Command History */}
        <div className="space-y-4 mb-6">
          {history.map((item, index) => (
            <div key={index} className="mb-5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[#58a6ff]">serhan@ergoren</span>
                <span className="text-[#6e7681]">:</span>
                <span className="text-[#79c0ff]">{currentPath}</span>
                <span className="text-[#6e7681]">$</span>
                <span className="text-[#c9d1d9]">{item.command}</span>
              </div>
              {item.output && (
                <div className="ml-0 text-[#8b949e] whitespace-pre-wrap leading-relaxed">
                  {item.output}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Input Line */}
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[#58a6ff]">serhan@ergoren</span>
            <span className="text-[#6e7681]">:</span>
            <span className="text-[#79c0ff]">{currentPath}</span>
            <span className="text-[#6e7681]">$</span>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent border-none outline-none text-[#c9d1d9] caret-[#58a6ff]"
            style={{ caretColor: '#58a6ff' }}
            autoFocus
            autoComplete="off"
            spellCheck="false"
          />
        </form>
      </div>
    </div>
  );
};

export default Terminal;

